import Http from '../../../network/Http'
import FacilityForReservation from '../../facilities/FacilityForReservation'
import {ApiResponse} from '../../../network/response/ApiResponse'
import {PaymentInformationForReservation, ReservationOption, RoomNumberView} from '../ReserveFacilityModel'
import {ContentType} from '../../../network/Constants'
import {Nullable} from '../../../app/TypeHelpers'
import AdminReserveAvailabilityDuration from './AdminReserveAvailabilityDuration'
import AdminReserveAvailabilityOvernight from './AdminReserveAvailabilityOvernight'

interface NewReservationInfoCommonPart {
    reservationTitle: string,
    options: ReservationOption[],
    sharedUseRoomNumberList: RoomNumberView[],
    userId: string,
    acceptedWarnings: string[],
}

interface NewAgentReservationInfoPart {
    userRidgeCd: string,
    userRoomNumber: string,
    paymentInformation: PaymentInformationForReservation,
    cashPaymentStatus: Nullable<string>,
}

export interface NewOvernightRentalFacilityReservationInfoPart {
    startDate: string,
    stayCount: number,
}

export interface NewDurationRentalFacilityReservationInfoPart {
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string,
}

export type NewAgentReservationForOvernight =
    NewReservationInfoCommonPart & NewAgentReservationInfoPart & NewOvernightRentalFacilityReservationInfoPart

export type NewAgentReservationForDuration =
    NewReservationInfoCommonPart & NewAgentReservationInfoPart & NewDurationRentalFacilityReservationInfoPart

export type NewManagerReservationForOvernight =
    NewReservationInfoCommonPart & NewOvernightRentalFacilityReservationInfoPart

export type NewManagerReservationForDuration =
    NewReservationInfoCommonPart & NewDurationRentalFacilityReservationInfoPart

export class ReservationsRepositoryForAdmin {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    async getFacilityForReservation(
        condominiumCd: string,
        facilityId: number,
    ): Promise<FacilityForReservation> {
        const responseJson: ApiResponse<FacilityForReservation> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities-for-reservation/${facilityId}`)
        const apiResponse: ApiResponse<FacilityForReservation> = ApiResponse.from(
            responseJson,
            FacilityForReservation.from,
        )
        return apiResponse.details
    }

    async registerAgentReserveFacilityForOvernight(
        condominiumCd: string,
        facilityId: number,
        reservation: NewAgentReservationForOvernight,
    ): Promise<ApiResponse<void>> {
        const response = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/agent/reservations-for-overnight`,
            reservation,
            ContentType.JSON,
        )
        return ApiResponse.from(response, t => t)
    }

    async registerAgentReserveFacilityForDuration(
        condominiumCd: string,
        facilityId: number,
        reservation: NewAgentReservationForDuration,
    ): Promise<ApiResponse<void>> {
        const response = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/agent/reservations-for-duration`,
            reservation,
            ContentType.JSON,
        )
        return ApiResponse.from(response, t => t)
    }

    async registerManagerReserveFacilityForOvernight(
        condominiumCd: string,
        facilityId: number,
        reservation: NewManagerReservationForOvernight,
    ): Promise<ApiResponse<void>> {
        const response = await this.http.post(
            `/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/manager/reservations-for-overnight`,
            reservation,
            ContentType.JSON,
        )
        return ApiResponse.from(response, t => t)
    }

    async registerManagerReserveFacilityForDuration(
        condominiumCd: string,
        facilityId: number,
        reservation: NewManagerReservationForDuration,
    ): Promise<ApiResponse<void>> {
        const response =
            await this.http.post(
                `/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}/manager/reservations-for-duration`,
                reservation,
                ContentType.JSON,
            )

        return ApiResponse.from(response, t => t)
    }

    async getReserveStatusForDurationRental(
        condominiumCd: string,
        facilityId: number,
        startDateTimeString: string,
        endDateTimeString: string,
    ): Promise<AdminReserveAvailabilityDuration> {
        const responseJson: ApiResponse<AdminReserveAvailabilityDuration> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}`
                + `/reserve-availabilities-for-duration?`
                + `startDateTime=${startDateTimeString}`
                + `&endDateTime=${endDateTimeString}`
            )

        const apiResponse: ApiResponse<AdminReserveAvailabilityDuration> = ApiResponse.from(
            responseJson,
            AdminReserveAvailabilityDuration.from,
        )
        return apiResponse.details
    }

    async getReserveStatusForOvernight(
        condominiumCd: string, facilityId: number, reservationStartDate: string, daysCount: number,
    ): Promise<AdminReserveAvailabilityOvernight> {
        const responseJson: ApiResponse<AdminReserveAvailabilityOvernight> =
            await this.http.get(`/api/admin/condominiums/${condominiumCd}/facilities/${facilityId}`
                + `/reserve-availabilities-for-overnight?`
                + `startDate=${reservationStartDate}`
                + `&count=${daysCount}`)

        const apiResponse: ApiResponse<AdminReserveAvailabilityOvernight> = ApiResponse.from(
            responseJson,
            AdminReserveAvailabilityOvernight.from,
        )
        return apiResponse.details
    }
}
